import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Zap, Plus, Trash2, Loader2, Bot, BellRing, Ticket, PauseCircle, GitBranch,
  Mail, Smartphone, Megaphone, History, ExternalLink, Play, Info, Sparkles,
} from 'lucide-react';
import { Header } from '../components/Header';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { cn } from '../lib/utils';
import { EscalationWindowCard } from '../components/EscalationWindowCard';
import { DeferredEscalations } from '../components/DeferredEscalations';
import { toast } from 'sonner';
import axios from 'axios';
import { EmptyState } from '../components/EmptyState';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_META = {
  notify_human: { Icon: BellRing, color: 'text-amber-700 dark:text-amber-400', label: 'Notificar humano' },
  create_ticket: { Icon: Ticket, color: 'text-brand-deep dark:text-brand-green', label: 'Crear ticket' },
  pause_noah: { Icon: PauseCircle, color: 'text-amber-700 dark:text-amber-400', label: 'Pausar Noah' },
  change_stage: { Icon: GitBranch, color: 'text-brand-deep dark:text-brand-deep dark:text-brand-green', label: 'Cambiar etapa' },
  send_email: { Icon: Mail, color: 'text-brand-deep dark:text-brand-green', label: 'Enviar email' },
  send_sms: { Icon: Smartphone, color: 'text-fuchsia-700 dark:text-fuchsia-300', label: 'Enviar SMS' },
};

const STAGES = ['lead', 'prospect', 'qualified', 'negotiation', 'closed_won', 'closed_lost'];
const PRIORITIES = ['low', 'normal', 'high', 'urgent'];

const TEMPLATES = [
  {
    name: 'Lead caliente > $500 → avisar a un humano',
    event: 'customer.created',
    conditions: [{ field: 'customer.value', op: 'gt', value: '500' }],
    actions: [
      { type: 'notify_human', params: { title: '🔥 Lead con presupuesto alto' } },
      { type: 'pause_noah', params: {} },
    ],
  },
  {
    name: 'Cliente pide hablar con una persona',
    event: 'message.received',
    conditions: [{ field: 'message.text', op: 'contains', value: 'humano' }],
    actions: [
      { type: 'pause_noah', params: {} },
      { type: 'create_ticket', params: { subject: 'Solicita atención humana', priority: 'high', category: 'support' } },
    ],
  },
  {
    name: 'Reclamo detectado → ticket urgente',
    event: 'message.received',
    conditions: [{ field: 'message.text', op: 'contains', value: 'reclamo' }],
    actions: [
      { type: 'create_ticket', params: { subject: 'Reclamo de cliente', priority: 'urgent', category: 'complaint' } },
      { type: 'notify_human', params: { title: '⚠️ Reclamo entrante' } },
    ],
  },
];

const emptyRule = (campaignId) => ({
  campaign_id: campaignId || '',
  name: '',
  event: 'message.received',
  match: 'all',
  conditions: [{ field: 'customer.value', op: 'gt', value: '500' }],
  actions: [{ type: 'notify_human', params: { title: '' } }],
  enabled: true,
});

export default function Automations() {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState('');
  const [rules, setRules] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState(emptyRule(''));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/automations/catalog`).then((r) => setCatalog(r.data)).catch(() => {});
    axios.get(`${API}/campaigns`).then((r) => {
      setCampaigns(r.data || []);
      if (r.data?.length) setCampaignId(r.data[0].id);
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/automations?campaign_id=${campaignId}`),
        axios.get(`${API}/automations/logs?campaign_id=${campaignId}&limit=20`).catch(() => ({ data: { logs: [] } })),
      ]);
      setRules(r1.data.rules || []);
      setLogs(r2.data.logs || []);
    } catch {
      toast.error('No se pudieron cargar las automatizaciones');
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => { load(); }, [load]);

  const fieldMeta = (id) => catalog?.fields?.find((f) => f.id === id);

  const openNew = (template) => {
    setDraft(template
      ? { ...emptyRule(campaignId), ...template, campaign_id: campaignId }
      : emptyRule(campaignId));
    setShowForm(true);
  };

  const save = async () => {
    if (!draft.name.trim()) { toast.error('Ponle un nombre a la regla'); return; }
    if (!draft.actions.length) { toast.error('Agrega al menos una acción'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/automations`, { ...draft, campaign_id: campaignId });
      toast.success('Regla creada — Noah ya la está vigilando');
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const toggleRule = async (rule) => {
    setRules((prev) => prev.map((r) => (r.id === rule.id ? { ...r, enabled: !r.enabled } : r)));
    try {
      await axios.put(`${API}/automations/${rule.id}`, { enabled: !rule.enabled });
    } catch {
      toast.error('No se pudo actualizar');
      load();
    }
  };

  const removeRule = async (rule) => {
    try {
      await axios.delete(`${API}/automations/${rule.id}`);
      toast.success('Regla eliminada');
      load();
    } catch { toast.error('Error al eliminar'); }
  };

  const testRule = async (rule) => {
    try {
      const res = await axios.post(`${API}/automations/test`, {
        rule_id: rule.id,
        sample: {
          customer: { id: 'demo', name: 'Cliente demo', value: 1200, status: 'lead', email: 'demo@x.com', phone: '593999' },
          chat: { id: 'demo-chat' },
          message: { text: 'Quiero hablar con un humano, tengo un reclamo', platform: 'whatsapp' },
          intelligence: { budget: 1200, urgency: 'alta' },
        },
      });
      res.data.matched
        ? toast.success(`Coincide ✓ ejecutaría: ${res.data.would_run.join(', ')}`)
        : toast.info('El contexto de ejemplo no cumple las condiciones');
    } catch { toast.error('Error en la prueba'); }
  };

  const updateCondition = (i, patch) =>
    setDraft((d) => ({ ...d, conditions: d.conditions.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) }));
  const updateAction = (i, patch) =>
    setDraft((d) => ({ ...d, actions: d.actions.map((a, idx) => (idx === i ? { ...a, ...patch } : a)) }));

  const activeCampaign = campaigns.find((c) => c.id === campaignId);

  return (
    <div className="min-h-screen bg-background" data-testid="automations-page">
      <Header title="Automatizaciones de Noah" subtitle="Reglas aisladas por campaña: si pasa esto, Noah hace aquello" />

      <div className="p-4 sm:p-6 space-y-5">
        {/* Campaign scope */}
        <Card className="border-border/60">
          <CardContent className="p-3 flex flex-wrap items-center gap-2">
            <Megaphone className="w-4 h-4 text-fuchsia-700 dark:text-fuchsia-300" />
            <span className="text-xs text-muted-foreground">Campaña:</span>
            <Select value={campaignId} onValueChange={setCampaignId}>
              <SelectTrigger className="w-[230px] h-9 text-xs" data-testid="automations-campaign-select">
                <SelectValue placeholder="Elige una campaña" />
              </SelectTrigger>
              <SelectContent>
                {campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {activeCampaign && (
              <button
                data-testid="automations-campaign-link"
                onClick={() => navigate('/campaigns')}
                className="inline-flex items-center gap-1 text-xs text-brand-deep dark:text-brand-deep dark:text-brand-green hover:underline"
              >
                Ver briefing de la campaña <ExternalLink className="w-3 h-3" />
              </button>
            )}
            <Button size="sm" className="ml-auto gap-1 bg-primary"
              onClick={() => openNew(null)} data-testid="automations-new-rule">
              <Plus className="w-4 h-4" /> Nueva regla
            </Button>
          </CardContent>
        </Card>

        {/* Reglas por horario de la campaña */}
        {campaignId && <EscalationWindowCard campaignId={campaignId} />}
        {campaignId && <DeferredEscalations campaignId={campaignId} />}

        {/* Templates */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-1">
            <Sparkles className="w-3.5 h-3.5 text-amber-700 dark:text-amber-400" /> Plantillas listas para usar
          </p>
          <div className="grid gap-3 md:grid-cols-3">
            {TEMPLATES.map((tpl) => (
              <button
                key={tpl.name}
                data-testid={`automation-template-${tpl.event}-${tpl.actions[0].type}`}
                onClick={() => openNew(tpl)}
                className="group text-left p-4 rounded-2xl bg-card border border-border/60 transition-[border-color,box-shadow] hover:border-violet-500/40 hover:shadow-lg hover:shadow-violet-500/5"
              >
                <p className="text-sm font-medium leading-snug">{tpl.name}</p>
                <div className="mt-2 flex flex-wrap gap-1">
                  {tpl.actions.map((a) => {
                    const meta = ACTION_META[a.type];
                    return (
                      <Badge key={a.type} variant="secondary" className="text-[10px] gap-1">
                        <meta.Icon className={cn('w-3 h-3', meta.color)} /> {meta.label}
                      </Badge>
                    );
                  })}
                </div>
                <span className="mt-2 inline-flex items-center gap-1 text-[11px] text-brand-deep dark:text-brand-deep dark:text-brand-green opacity-0 group-hover:opacity-100 transition-opacity">
                  Usar plantilla <ExternalLink className="w-3 h-3" />
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Rules */}
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-muted-foreground" /></div>
        ) : rules.length === 0 ? (
          <Card className="border-dashed border-border/60">
            <CardContent className="p-10 text-center space-y-2">
              <EmptyState
                mood="focused"
                title="Todavía no hay reglas en esta campaña"
                description="Usa una plantilla o crea tu primera regla para que Noah actúe solo: avisar, escalar a ticket o pausarse."
                testId="automations-empty"
              />
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="automations-list">
            {rules.map((rule) => (
              <Card key={rule.id} className="border-border/60" data-testid={`automation-rule-${rule.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Bot className="w-4 h-4 text-brand-deep dark:text-brand-deep dark:text-brand-green shrink-0" />
                        <p className="text-sm font-semibold truncate">{rule.name}</p>
                        <Badge variant="outline" className="text-[10px]">{rule.event}</Badge>
                      </div>
                      <p className="mt-1.5 text-xs text-muted-foreground">
                        Si <b>{rule.match === 'all' ? 'TODAS' : 'ALGUNA'}</b> de{' '}
                        {(rule.conditions || []).map((c, i) => (
                          <span key={i} className="text-foreground">
                            {i > 0 && ', '}{fieldMeta(c.field)?.label || c.field} {catalog?.operators?.find((o) => o.id === c.op)?.label} {c.value}
                          </span>
                        ))}
                        {(rule.conditions || []).length === 0 && <span>cualquier evento</span>}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {(rule.actions || []).map((a, i) => {
                          const meta = ACTION_META[a.type] || ACTION_META.notify_human;
                          return (
                            <Badge key={i} variant="secondary" className="text-[10px] gap-1">
                              <meta.Icon className={cn('w-3 h-3', meta.color)} /> {meta.label}
                            </Badge>
                          );
                        })}
                      </div>
                      <p className="mt-2 text-[11px] text-muted-foreground">
                        Disparada {rule.triggered_count || 0} veces
                        {rule.last_triggered_at && ` · última: ${new Date(rule.last_triggered_at).toLocaleString('es')}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Switch checked={rule.enabled} onCheckedChange={() => toggleRule(rule)} data-testid={`automation-toggle-${rule.id}`} />
                      <Button size="icon" variant="ghost" onClick={() => testRule(rule)} data-testid={`automation-test-${rule.id}`}>
                        <Play className="w-4 h-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => removeRule(rule)} data-testid={`automation-delete-${rule.id}`}>
                        <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Activity log */}
        <Card className="border-border/60">
          <CardContent className="p-4">
            <p className="text-sm font-semibold flex items-center gap-2 mb-3">
              <History className="w-4 h-4 text-brand-deep dark:text-brand-green" /> Últimas ejecuciones
              <button onClick={() => navigate('/tickets')} className="ml-auto text-[11px] text-brand-deep dark:text-brand-deep dark:text-brand-green hover:underline inline-flex items-center gap-1" data-testid="automations-tickets-link">
                Ver tickets generados <ExternalLink className="w-3 h-3" />
              </button>
            </p>
            {logs.length === 0 ? (
              <p className="text-xs text-muted-foreground flex items-center gap-1"><Info className="w-3.5 h-3.5" /> Aún no se ha disparado ninguna regla en esta campaña.</p>
            ) : (
              <ScrollArea className="h-[220px] pr-3">
                <div className="space-y-2" data-testid="automations-logs">
                  {logs.map((l) => (
                    <div key={l.id} className="p-2.5 rounded-lg bg-muted/20 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{l.rule_name}</span>
                        <span className="text-muted-foreground">{new Date(l.at).toLocaleString('es')}</span>
                      </div>
                      <p className="text-muted-foreground mt-0.5">{(l.results || []).join(' · ')}</p>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Rule builder */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="automation-form-dialog">
          <DialogHeader><DialogTitle>Nueva regla de Noah</DialogTitle></DialogHeader>

          <div className="space-y-5">
            <div>
              <Label className="text-xs">Nombre de la regla</Label>
              <Input className="mt-1" value={draft.name} data-testid="automation-name-input"
                placeholder="Lead caliente → avisar a un humano"
                onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Cuándo (evento)</Label>
                <Select value={draft.event} onValueChange={(v) => setDraft({ ...draft, event: v })}>
                  <SelectTrigger className="mt-1 h-9 text-xs" data-testid="automation-event-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {catalog?.events?.map((e) => <SelectItem key={e.id} value={e.id}>{e.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Coincidencia</Label>
                <Select value={draft.match} onValueChange={(v) => setDraft({ ...draft, match: v })}>
                  <SelectTrigger className="mt-1 h-9 text-xs" data-testid="automation-match-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Se cumplen TODAS</SelectItem>
                    <SelectItem value="any">Se cumple ALGUNA</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Conditions */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Condiciones</Label>
                <Button size="sm" variant="outline" data-testid="automation-add-condition"
                  onClick={() => setDraft({ ...draft, conditions: [...draft.conditions, { field: 'message.text', op: 'contains', value: '' }] })}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Añadir
                </Button>
              </div>
              {draft.conditions.map((c, i) => (
                <div key={i} className="flex flex-wrap gap-2 items-center p-2 rounded-xl bg-muted/20">
                  <Select value={c.field} onValueChange={(v) => updateCondition(i, { field: v, value: '' })}>
                    <SelectTrigger className="w-[190px] h-9 text-xs" data-testid={`automation-condition-field-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {catalog?.fields?.map((f) => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={c.op} onValueChange={(v) => updateCondition(i, { op: v })}>
                    <SelectTrigger className="w-[150px] h-9 text-xs" data-testid={`automation-condition-op-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {catalog?.operators?.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {c.op !== 'exists' && (
                    fieldMeta(c.field)?.type === 'enum' ? (
                      <Select value={c.value || ''} onValueChange={(v) => updateCondition(i, { value: v })}>
                        <SelectTrigger className="w-[150px] h-9 text-xs" data-testid={`automation-condition-value-${i}`}><SelectValue placeholder="Valor" /></SelectTrigger>
                        <SelectContent>
                          {(fieldMeta(c.field)?.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input className="w-[150px] h-9 text-xs" value={c.value || ''} placeholder="Valor"
                        data-testid={`automation-condition-value-${i}`}
                        onChange={(e) => updateCondition(i, { value: e.target.value })} />
                    )
                  )}
                  <Button size="icon" variant="ghost" data-testid={`automation-remove-condition-${i}`}
                    onClick={() => setDraft({ ...draft, conditions: draft.conditions.filter((_, idx) => idx !== i) })}>
                    <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                  </Button>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Entonces Noah hace</Label>
                <Button size="sm" variant="outline" data-testid="automation-add-action"
                  onClick={() => setDraft({ ...draft, actions: [...draft.actions, { type: 'notify_human', params: {} }] })}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Añadir
                </Button>
              </div>
              {draft.actions.map((a, i) => (
                <div key={i} className="p-2.5 rounded-xl bg-muted/20 space-y-2">
                  <div className="flex gap-2 items-center">
                    <Select value={a.type} onValueChange={(v) => updateAction(i, { type: v, params: {} })}>
                      <SelectTrigger className="flex-1 h-9 text-xs" data-testid={`automation-action-type-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {catalog?.actions?.map((x) => <SelectItem key={x.id} value={x.id}>{x.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button size="icon" variant="ghost" data-testid={`automation-remove-action-${i}`}
                      onClick={() => setDraft({ ...draft, actions: draft.actions.filter((_, idx) => idx !== i) })}>
                      <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                    </Button>
                  </div>

                  {a.type === 'notify_human' && (
                    <Input className="h-9 text-xs" placeholder="Título de la notificación"
                      data-testid={`automation-action-title-${i}`}
                      value={a.params?.title || ''}
                      onChange={(e) => updateAction(i, { params: { ...a.params, title: e.target.value } })} />
                  )}

                  {a.type === 'create_ticket' && (
                    <div className="flex flex-wrap gap-2">
                      <Input className="flex-1 min-w-[160px] h-9 text-xs" placeholder="Asunto del ticket"
                        data-testid={`automation-action-subject-${i}`}
                        value={a.params?.subject || ''}
                        onChange={(e) => updateAction(i, { params: { ...a.params, subject: e.target.value } })} />
                      <Select value={a.params?.priority || 'high'} onValueChange={(v) => updateAction(i, { params: { ...a.params, priority: v } })}>
                        <SelectTrigger className="w-[120px] h-9 text-xs" data-testid={`automation-action-priority-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}

                  {a.type === 'change_stage' && (
                    <Select value={a.params?.status || ''} onValueChange={(v) => updateAction(i, { params: { ...a.params, status: v } })}>
                      <SelectTrigger className="h-9 text-xs" data-testid={`automation-action-stage-${i}`}><SelectValue placeholder="Nueva etapa" /></SelectTrigger>
                      <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  )}

                  {(a.type === 'send_email' || a.type === 'send_sms') && (
                    <Input className="h-9 text-xs" placeholder="Mensaje a enviar"
                      data-testid={`automation-action-body-${i}`}
                      value={a.params?.body || ''}
                      onChange={(e) => updateAction(i, { params: { ...a.params, body: e.target.value } })} />
                  )}
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setShowForm(false)} data-testid="automation-cancel">Cancelar</Button>
            <Button size="sm" onClick={save} disabled={saving} data-testid="automation-save"
              className="bg-primary">
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Guardar regla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
